import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'ar' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isRtl: boolean;
}

const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Navbar
    'nav.home': 'الرئيسية',
    'nav.browse': 'تصفح العقارات',
    'nav.favorites': 'مفضلتي',
    'nav.edit_profile': 'تعديل حسابي',
    'nav.dashboard': 'لوحة التحكم',
    'nav.logout': 'تسجيل الخروج',
    'nav.login': 'تسجيل الدخول / مالك',
    'nav.login_short': 'دخول',
    'nav.list_property': 'أضف عقارك مجاناً',
    'nav.list_property_short': 'أضف عقار',
    'nav.certified_member': 'عضو موثق',
    'nav.registered_member': 'عضو مسجل',
    'nav.owner': 'المالك',

    // Hero Section
    'hero.badge': 'سوق مُلاك • المنصة الأولى للعقارات من المالك مباشرة',
    'hero.title1': 'اعثر على عقارك',
    'hero.title2': 'مباشرة من المالك',
    'hero.desc': 'منصة سوق مُلاك تجمع العقارات المعروضة من المالك مباشرة لضمان تجربة آمنة وشفافة وبدون أي عمولات سعي تقليدية مرهقة.',
    'hero.btn_browse': 'ابدأ تصفح العقارات',
    'hero.btn_add': 'أضف عقارك مجاناً',

    // Search / Filters
    'search.governorate': 'المحافظة',
    'search.city': 'الولاية / المدينة',
    'search.type': 'نوع العقار',
    'search.deal_type': 'نوع العرض',
    'search.min_price': 'السعر الأدنى (ر.ع)',
    'search.max_price': 'السعر الأعلى (ر.ع)',
    'search.min_area': 'المساحة الأدنى (م²)',
    'search.max_area': 'المساحة الأعلى (م²)',
    'search.btn_search': 'ابحث الآن عن عقارك',
    'search.btn_reset': 'إعادة تعيين الفلاتر',
    'search.placeholder_all': 'الكل',
    'search.all_governorates': 'كل المحافظات',
    'search.all_cities': 'كل الولايات / المدن',
    'search.all_types': 'كل الأنواع',
    'search.all_deals': 'كل العروض',
    'search.results_found': 'تم العثور على {count} عقار مميز يطابق بحثك',
    'search.no_results': 'لم نجد أي عقارات تطابق خيارات البحث الحالية.',
    'search.show_all': 'عرض جميع العقارات المتاحة',

    // Deal Types
    'deal.sale': 'للبيع',
    'deal.rent': 'للإيجار',

    // Categories / Types
    'cat.apartment': 'شقة',
    'cat.villa': 'فيلا / منزل',
    'cat.land': 'أرض',
    'cat.commercial': 'مكتب / تجاري',
    'cat.building': 'بناية كاملة',
    'cat.chalet': 'شاليه / استراحة',

    // Property Card
    'card.views': 'مشاهدة',
    'card.contact_whatsapp': 'تواصل واتساب',
    'card.contact_call': 'اتصال مباشر',
    'card.details': 'التفاصيل والملكية',
    'card.verified': 'عقار موثق',
    'card.premium': 'إعلان مميز',
    'card.currency': 'ر.ع',
    'card.area': 'م²',
    'card.rooms': 'غرف',
    'card.baths': 'دورات مياه',
    'card.added_since': 'منذ {time}',

    // Property Details Modal
    'details.title': 'تفاصيل العقار',
    'details.code': 'رمز العقار',
    'details.owner_info': 'بيانات المالك والموثوقية',
    'details.certified_owner': 'مالك معتمد وموثق الهوية بالمنصة',
    'details.unverified_owner': 'مالك مسجل (بانتظار توثيق الهوية)',
    'details.description': 'وصف العقار التفصيلي',
    'details.features': 'المزايا والمرافق الإضافية',
    'details.location_map': 'الموقع الجغرافي للعقار',
    'details.contact_btn': 'تواصل مباشر مع المالك',
    'details.whatsapp_msg': 'مرحباً، أنا مهتم بعقارك رقم {code} ({title}) المعروض في سوق مُلاك.',

    // Footer
    'footer.rights': 'جميع الحقوق محفوظة © سوق مُلاك',
    'footer.desc': 'المنصة العمانية الأولى لربط المشتري بالمالك مباشرة دون وسطاء أو عمولات إضافية.',
    'footer.links_title': 'روابط سريعة',
    'footer.terms': 'الشروط والأحكام',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.support': 'الدعم الفني والشكاوى',

    // User Dashboard
    'dash.welcome': 'مرحباً بك في لوحة الملاك 👋',
    'dash.welcome_desc': 'تتبع وعاين إحصائيات عقاراتك، وثق هويتك، وقم بترقية اشتراكاتك بسهولة.',
    'dash.your_properties': 'عقاراتك المعلنة',
    'dash.account_status': 'حالة الحساب',
    'dash.verified': 'موثق ومعتمد ✓',
    'dash.unverified': 'بحاجة لتوثيق ⚠️',
    'dash.plan_free': 'الباقة المجانية',
    'dash.plan_pro': 'الباقة الاحترافية',
    'dash.menu_title': 'إعدادات المعلن والخدمات',
    'dash.tab_overview': 'لوحة الإحصائيات والمشاهدات',
    'dash.tab_overview_desc': 'تحليل المشاهدات والتفاعل لعقاراتك المضافة',
    'dash.tab_properties': 'إعلاناتي العقارية والطلبات',
    'dash.tab_properties_desc': 'إدارة، تعديل، وحذف إعلاناتك العقارية المضافة',
    'dash.tab_verification': 'توثيق الهوية',
    'dash.tab_verification_desc': 'رفع وثائق التحقق الرسمية',
    'dash.tab_subscription': 'الاشتراكات وترقية الباقات',
    'dash.tab_subscription_desc': 'ترقية خطة حسابك للحصول على ميزات إعلانية',
    'dash.tab_profile': 'تعديل الملف الشخصي والمعلومات',
    'dash.tab_profile_desc': 'الاسم، رقم الهاتف، والواتساب المباشر',
    'dash.tab_notifications': 'مركز التنبيهات والرسائل الواردة',
    'dash.tab_notifications_desc': 'الرسائل الجديدة والاستفسارات الواردة',
    'dash.logout_btn': 'تسجيل الخروج من الحساب الشخصي',
    'dash.back_home': 'رجوع للرئيسية',
    'dash.total_views': 'إجمالي مشاهدات عقاراتك',
    'dash.active_properties': 'عقاراتك النشطة حالياً',

    // Oman Map / Region
    'map.title': 'تصفح العقارات حسب محافظات سلطنة عُمان',
    'map.desc': 'اضغط على المحافظة لتصفية العقارات المعروضة فيها مباشرة لتسهيل البحث والوصول.',

    // Common buttons
    'btn.close': 'إغلاق',
    'btn.save': 'حفظ التغييرات',
    'btn.cancel': 'إلغاء',
    'btn.add_property': 'إضافة العقار الآن',
    'btn.edit_property': 'تعديل بيانات العقار',
    'btn.delete': 'حذف',
    'btn.confirm_delete': 'نعم، احذف',
  },
  en: {
    // Navbar
    'nav.home': 'Home',
    'nav.browse': 'Browse Properties',
    'nav.favorites': 'My Favorites',
    'nav.edit_profile': 'Edit Profile',
    'nav.dashboard': 'Dashboard',
    'nav.logout': 'Log Out',
    'nav.login': 'Sign In / Owner',
    'nav.login_short': 'Sign In',
    'nav.list_property': 'Add Property Free',
    'nav.list_property_short': 'Add Property',
    'nav.certified_member': 'Verified Member',
    'nav.registered_member': 'Registered Member',
    'nav.owner': 'Owner',

    // Hero Section
    'hero.badge': 'Sooq Mulak • The #1 platform for properties directly from the owner',
    'hero.title1': 'Find Your Property',
    'hero.title2': 'Directly From Owner',
    'hero.desc': 'Sooq Mulak connects property buyers with owners directly to guarantee a safe, transparent experience without any exhausting traditional brokerage fees.',
    'hero.btn_browse': 'Browse Properties Now',
    'hero.btn_add': 'List Your Property Free',

    // Search / Filters
    'search.governorate': 'Governorate',
    'search.city': 'City / State',
    'search.type': 'Property Type',
    'search.deal_type': 'Deal Type',
    'search.min_price': 'Min Price (OMR)',
    'search.max_price': 'Max Price (OMR)',
    'search.min_area': 'Min Area (m²)',
    'search.max_area': 'Max Area (m²)',
    'search.btn_search': 'Search for Your Property',
    'search.btn_reset': 'Reset Filters',
    'search.placeholder_all': 'All',
    'search.all_governorates': 'All Governorates',
    'search.all_cities': 'All Cities',
    'search.all_types': 'All Types',
    'search.all_deals': 'All Deals',
    'search.results_found': 'Found {count} premium properties matching your search',
    'search.no_results': 'We could not find any properties matching your search criteria.',
    'search.show_all': 'Show All Available Properties',

    // Deal Types
    'deal.sale': 'For Sale',
    'deal.rent': 'For Rent',

    // Categories / Types
    'cat.apartment': 'Apartment',
    'cat.villa': 'Villa / House',
    'cat.land': 'Land',
    'cat.commercial': 'Office / Commercial',
    'cat.building': 'Full Building',
    'cat.chalet': 'Chalet / Rest House',

    // Property Card
    'card.views': 'Views',
    'card.contact_whatsapp': 'WhatsApp Chat',
    'card.contact_call': 'Direct Call',
    'card.details': 'Details & Ownership',
    'card.verified': 'Verified Property',
    'card.premium': 'Premium Ad',
    'card.currency': 'OMR',
    'card.area': 'm²',
    'card.rooms': 'Beds',
    'card.baths': 'Baths',
    'card.added_since': 'Added {time}',

    // Property Details Modal
    'details.title': 'Property Details & Title Deed',
    'details.code': 'Property Code',
    'details.owner_info': 'Owner Info & Trust',
    'details.certified_owner': 'Certified & Identity-Verified Owner',
    'details.unverified_owner': 'Registered Owner (Pending Verification)',
    'details.description': 'Detailed Description',
    'details.features': 'Features & Amenities',
    'details.location_map': 'Geographical Location',
    'details.contact_btn': 'Direct Contact with Owner',
    'details.whatsapp_msg': 'Hello, I am interested in your property #{code} ({title}) listed on Sooq Mulak.',

    // Footer
    'footer.rights': 'All Rights Reserved © Sooq Mulak',
    'footer.desc': 'The first Omani platform connecting buyers to owners directly without brokers or extra commissions.',
    'footer.links_title': 'Quick Links',
    'footer.terms': 'Terms & Conditions',
    'footer.privacy': 'Privacy Policy',
    'footer.support': 'Technical Support',

    // User Dashboard
    'dash.welcome': 'Welcome to Owner Dashboard 👋',
    'dash.welcome_desc': 'Track property views, verify your identity, and upgrade your subscription seamlessly.',
    'dash.your_properties': 'Your Listed Properties',
    'dash.account_status': 'Account Status',
    'dash.verified': 'Verified & Approved ✓',
    'dash.unverified': 'Verification Needed ⚠️',
    'dash.plan_free': 'Free Plan',
    'dash.plan_pro': 'Pro Plan',
    'dash.menu_title': 'Owner Settings & Services',
    'dash.tab_overview': 'Statistics & Views Dashboard',
    'dash.tab_overview_desc': 'Analyze views and engagement for your added properties',
    'dash.tab_properties': 'My Listings & Requests',
    'dash.tab_properties_desc': 'Manage, edit, and delete your listed properties',
    'dash.tab_verification': 'Identity Verification & Title Deeds',
    'dash.tab_verification_desc': 'Upload official verification files and ownership documents',
    'dash.tab_subscription': 'Subscriptions & Plan Upgrades',
    'dash.tab_subscription_desc': 'Upgrade your plan to unlock premium advertising features',
    'dash.tab_profile': 'Edit Personal Profile & Info',
    'dash.tab_profile_desc': 'Name, phone number, and direct WhatsApp',
    'dash.tab_notifications': 'Notifications & Inquiries Hub',
    'dash.tab_notifications_desc': 'New updates and received inquiries',
    'dash.logout_btn': 'Log Out of Personal Account',
    'dash.back_home': 'Back to Homepage',
    'dash.total_views': 'Total Views of Your Listings',
    'dash.active_properties': 'Your Currently Active Listings',

    // Oman Map / Region
    'map.title': 'Browse Properties by Governorate in Oman',
    'map.desc': 'Click on any governorate to instantly filter listed properties in that region.',

    // Common buttons
    'btn.close': 'Close',
    'btn.save': 'Save Changes',
    'btn.cancel': 'Cancel',
    'btn.add_property': 'Add Property Now',
    'btn.edit_property': 'Edit Property Info',
    'btn.delete': 'Delete',
    'btn.confirm_delete': 'Yes, Delete',
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('souq_malak_language');
    return (saved === 'en' || saved === 'ar') ? (saved as Language) : 'ar';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('souq_malak_language', lang);
  };

  const isRtl = language === 'ar';

  useEffect(() => {
    // Dynamically update document direction and language attribute
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRtl]);

  const t = (key: string): string => {
    return translations[language][key] || translations['ar'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function translateGovernorate(name: string, lang: Language): string {
  if (lang === 'ar' || !name) return name;
  switch (name) {
    case 'مسقط': return 'Muscat';
    case 'ظفار': return 'Dhofar';
    case 'مسندم': return 'Musandam';
    case 'البريمي': return 'Al Buraimi';
    case 'الداخلية': return 'Al Dakhiliyah';
    case 'شمال الباطنة': return 'North Al Batinah';
    case 'جنوب الباطنة': return 'South Al Batinah';
    case 'جنوب الشرقية': return 'South Al Sharqiyah';
    case 'شمال الشرقية': return 'North Al Sharqiyah';
    case 'الظاهرة': return 'Al Dhahirah';
    case 'الوسطى': return 'Al Wusta';
    default: return name;
  }
}

export function translateCity(name: string, lang: Language): string {
  if (lang === 'ar' || !name) return name;
  switch (name) {
    case 'السيب': return 'Al Seeb';
    case 'بوشر': return 'Bawshar';
    case 'المطرح': return 'Muttrah';
    case 'مطرح': return 'Muttrah';
    case 'مسقط': return 'Muscat';
    case 'العامرات': return 'Al Amerat';
    case 'قريات': return 'Qurayyat';
    case 'صلالة': return 'Salalah';
    case 'طاقة': return 'Taqah';
    case 'مرباط': return 'Mirbat';
    case 'ثمريت': return 'Thumrait';
    case 'خصب': return 'Khasab';
    case 'نزوى': return 'Nizwa';
    case 'بهلاء': return 'Bahla';
    case 'سمائل': return 'Samail';
    case 'صحار': return 'Sohar';
    case 'صحم': return 'Saham';
    case 'السويق': return 'Suwayq';
    case 'الخابورة': return 'Al Khaburah';
    case 'الرستاق': return 'Rustaq';
    case 'بركاء': return 'Barka';
    case 'المصنعة': return 'Al Musannah';
    case 'إبراء': return 'Ibra';
    case 'المضيبي': return 'Al Mudhaibi';
    case 'بدية': return 'Bidiya';
    case 'صور': return 'Sur';
    case 'جعلان بني بو علي': return 'Jalan Bani Bu Ali';
    case 'جعلان بني بو حسن': return 'Jalan Bani Bu Hassan';
    case 'عبري': return 'Ibri';
    case 'ينقل': return 'Yanqul';
    case 'البريمي': return 'Al Buraimi';
    case 'الدقم': return 'Duqm';
    case 'هيما': return 'Haima';
    default: return name;
  }
}
